import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import {
  Bot,
  ChevronDown,
  KeyRound,
  MoreHorizontal,
  RotateCw,
  Trash2,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ROLES, type LocationGrantInput, type MemberDTO, type Role, type TeamDTO } from '@regent/shared';
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  Input,
  Select,
  SkeletonRows,
  toast,
} from '@/components/ui';
import { AccessPicker, PrincipalAccessDialog, describeGrants } from '@/features/hierarchy/access-picker';
import { useHierarchyIndex } from '@/features/hierarchy/api';
import { SettingsTabs } from '@/features/inbox/settings-tabs';
import { MemberTokensDialog } from '@/features/settings/member-tokens-dialog';
import { Page } from '@/features/shell/page';
import { api, errorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  invitesQuery,
  keys,
  useCan,
  useMe,
  useMembers,
  useTeams,
  useWorkspace,
  useWorkspaceId,
} from '@/lib/queries';
import { useOnlineIds } from '@/lib/realtime';

export const Route = createFileRoute('/w/$workspaceId/settings/members')({
  validateSearch: (search: Record<string, unknown>): { invite?: boolean } =>
    search.invite === true || search.invite === 'true' ? { invite: true } : {},
  component: MembersPage,
});

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
  { value: 'guest', label: 'Guest' },
];

const ROLE_HELP: Record<string, string> = {
  admin: 'Can manage members, settings and everything in the workspace.',
  member: 'Can create and edit work. Reaches every space that is not private, or only the places you choose.',
  guest: 'Sees only the places you choose below. Right for freelancers and clients.',
};

/** How far a member reaches: everything open, or only the places picked for them. */
type Reach = 'all' | 'chosen';
const REACH_OPTIONS: { value: Reach; label: string }[] = [
  { value: 'all', label: 'Everything that is not private' },
  { value: 'chosen', label: 'Only the places chosen below' },
];

/** Whether the teams they join hand them their places, or access is set by hand. */
type TeamAccess = 'inherit' | 'manual';
const TEAM_ACCESS_OPTIONS: { value: TeamAccess; label: string }[] = [
  { value: 'inherit', label: "Inherit the team's access" },
  { value: 'manual', label: 'Set their access by hand' },
];
const TEAM_ACCESS_HELP: Record<TeamAccess, string> = {
  inherit: 'They reach everything their teams can reach, on top of what their role allows.',
  manual:
    'Their teams give them no places. They reach what their role allows plus the places you tick below.',
};

/** Picks the teams a person belongs to. */
function TeamsPicker({
  teams,
  value,
  onChange,
}: {
  teams: TeamDTO[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const chosen = teams.filter((t) => value.includes(t.id));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full justify-between font-normal" aria-label="Teams">
          <span className={cn('truncate', !chosen.length && 'text-fg-subtle')}>
            {chosen.length ? chosen.map((t) => t.name).join(', ') : 'No teams'}
          </span>
          <ChevronDown className="shrink-0 text-fg-subtle" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {teams.map((t) => (
          <DropdownMenuCheckboxItem
            key={t.id}
            checked={value.includes(t.id)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              onChange(checked ? [...value, t.id] : value.filter((id) => id !== t.id))
            }
          >
            <span
              aria-hidden
              className="mr-1.5 inline-block size-2 rounded-full"
              style={{ background: t.color }}
            />
            {t.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const ws = useWorkspaceId();
  const qc = useQueryClient();
  const teams = useTeams().data ?? [];
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [reach, setReach] = useState<Reach>('all');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [teamAccess, setTeamAccess] = useState<TeamAccess>('inherit');
  const [grants, setGrants] = useState<LocationGrantInput[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const limited = role === 'member' && reach === 'chosen';
  // Admins reach everything, so teams are just membership for them.
  const askTeamAccess = teamIds.length > 0 && role !== 'admin';
  const manual = askTeamAccess && teamAccess === 'manual';
  // Guests, limited members and people whose teams add nothing get exactly the places picked.
  const picking = role === 'guest' || limited || manual;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await api.post(`/w/${ws}/invites`, {
        email: email.trim(),
        role,
        limited,
        grants: picking ? grants : [],
        teamIds,
        inheritTeamAccess: !manual,
      });
      await qc.invalidateQueries({ queryKey: keys.invites(ws) });
      toast.success('Invite sent', `${email.trim()} will get an email with a link to join.`);
      setEmail('');
      setGrants([]);
      setReach('all');
      setTeamIds([]);
      setTeamAccess('inherit');
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size={picking ? 'md' : 'sm'}>
        <DialogHeader
          title="Invite people"
          description="They will get an email with a link to join this workspace."
        />
        <form onSubmit={submit} className="space-y-3.5" noValidate>
          <Field label="Email" htmlFor="invite-email" error={error}>
            <Input
              id="invite-email"
              type="email"
              autoFocus
              required
              placeholder="teammate@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Role" hint={ROLE_HELP[role]}>
            <Select
              aria-label="Role"
              value={role}
              onChange={setRole}
              options={ROLE_OPTIONS.filter((r) => r.value !== 'owner')}
              className="w-full"
            />
          </Field>
          {role === 'member' ? (
            <Field label="Access">
              <Select
                aria-label="Access"
                value={reach}
                onChange={setReach}
                options={REACH_OPTIONS}
                className="w-full"
              />
            </Field>
          ) : null}
          {teams.length ? (
            <Field
              label="Teams"
              hint={teamIds.length ? 'They join these teams when they accept.' : 'Optional.'}
            >
              <TeamsPicker teams={teams} value={teamIds} onChange={setTeamIds} />
            </Field>
          ) : null}
          {askTeamAccess ? (
            <Field label="Team access" hint={TEAM_ACCESS_HELP[teamAccess]}>
              <Select
                aria-label="Team access"
                value={teamAccess}
                onChange={setTeamAccess}
                options={TEAM_ACCESS_OPTIONS}
                className="w-full"
              />
            </Field>
          ) : null}
          {picking ? (
            <Field label="What they can reach">
              <AccessPicker value={grants} onChange={setGrants} />
            </Field>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={pending} disabled={!email.trim()}>
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MemberRow({ member, online }: { member: MemberDTO; online: boolean }) {
  const ws = useWorkspaceId();
  const qc = useQueryClient();
  const me = useMe();
  const workspace = useWorkspace();
  const teams = (useTeams().data ?? []).filter((t) => member.teamIds.includes(t.id));
  const canUpdate = useCan('members.update');
  const canRemove = useCan('members.remove');
  const isSelf = member.id === me?.user.id;
  const [access, setAccess] = useState(false);
  const [tokens, setTokens] = useState(false);
  // An owner or admin may hold tokens for members below their own role (bot accounts).
  const canHoldTokens =
    canUpdate && !isSelf && Boolean(workspace) && ROLES.indexOf(member.role) > ROLES.indexOf(workspace!.role);
  // Owners and admins reach everything; access is set per place for everyone else.
  const canSetAccess = canUpdate && member.role !== 'owner' && member.role !== 'admin';
  // Only owners can change owner roles.
  const roleLocked = !canUpdate || (member.role === 'owner' && workspace?.role !== 'owner');
  const options = ROLE_OPTIONS.filter((r) => r.value !== 'owner' || workspace?.role === 'owner');

  const changeRole = async (role: Role) => {
    try {
      await api.patch(`/w/${ws}/members/${member.id}`, { role });
      await qc.invalidateQueries({ queryKey: keys.members(ws) });
      if (isSelf) await qc.invalidateQueries({ queryKey: keys.me });
      toast.success(`${member.name} is now ${role === 'owner' || role === 'admin' ? 'an' : 'a'} ${role}`);
    } catch (err) {
      toast.error(err);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/w/${ws}/members/${member.id}`);
      await qc.invalidateQueries({ queryKey: keys.members(ws) });
      toast.success(`Removed ${member.name} from the workspace`);
    } catch (err) {
      toast.error(err);
    }
  };

  return (
    <tr className="border-b border-border last:border-0" data-testid={`member-${member.email}`}>
      <td className="py-2.5 pr-3 pl-4">
        <div className="flex items-center gap-3">
          <Avatar name={member.name} id={member.id} image={member.image} online={online} />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">
              {member.name} {isSelf ? <span className="font-normal text-fg-subtle">(you)</span> : null}
            </p>
            <p className="truncate text-xs text-fg-muted">{member.email}</p>
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5">
        {online ? (
          <Badge tone="success">Online</Badge>
        ) : (
          <span
            className="text-xs text-fg-subtle"
            title={member.lastSeenAt ? new Date(member.lastSeenAt).toLocaleString() : undefined}
          >
            {member.lastSeenAt
              ? `Offline, active ${formatDistanceToNow(member.lastSeenAt, { addSuffix: true })}`
              : 'Offline, never signed in'}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {roleLocked ? (
          <Badge>{ROLE_OPTIONS.find((r) => r.value === member.role)?.label}</Badge>
        ) : (
          <Select
            aria-label={`Role for ${member.name}`}
            size="sm"
            value={member.role}
            onChange={changeRole}
            options={options}
          />
        )}
        {member.limited ? (
          <p className="mt-1 text-xs text-fg-subtle" data-testid="member-limited">
            Only chosen places
          </p>
        ) : null}
        {teams.length ? (
          <p className="mt-1 truncate text-xs text-fg-subtle" data-testid="member-teams">
            {teams.map((t) => t.name).join(', ')}
            {!member.inheritTeamAccess && member.role !== 'owner' && member.role !== 'admin'
              ? ' (own access only)'
              : ''}
          </p>
        ) : null}
      </td>
      <td className="py-2.5 pr-4 pl-3 text-right">
        {(canRemove && !isSelf) || canSetAccess || canHoldTokens ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon-sm" variant="ghost" aria-label={`Actions for ${member.name}`}>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canSetAccess ? (
                <DropdownMenuItem onSelect={() => setAccess(true)}>
                  <KeyRound /> Access to places
                </DropdownMenuItem>
              ) : null}
              {canHoldTokens ? (
                <DropdownMenuItem onSelect={() => setTokens(true)}>
                  <Bot /> API tokens
                </DropdownMenuItem>
              ) : null}
              {canRemove && !isSelf ? (
                <DropdownMenuItem danger onSelect={() => void remove()}>
                  <UserMinus /> Remove from workspace
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </td>
      {access ? (
        <PrincipalAccessDialog
          principal={{ type: 'user', id: member.id, name: member.name }}
          member={{
            role: member.role,
            limited: member.limited,
            inheritTeamAccess: member.inheritTeamAccess,
            teamCount: member.teamIds.length,
          }}
          onClose={() => setAccess(false)}
        />
      ) : null}
      {tokens ? <MemberTokensDialog member={member} onClose={() => setTokens(false)} /> : null}
    </tr>
  );
}

function PendingInvites() {
  const ws = useWorkspaceId();
  const qc = useQueryClient();
  const index = useHierarchyIndex();
  const teams = useTeams().data ?? [];
  const invites = useQuery(invitesQuery(ws));
  if (!invites.data?.length) return null;
  const teamNames = (ids: string[]) =>
    ids
      .map((id) => teams.find((t) => t.id === id)?.name)
      .filter(Boolean)
      .join(', ');

  const act = async (fn: () => Promise<unknown>, message: string) => {
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: keys.invites(ws) });
      toast.success(message);
    } catch (err) {
      toast.error(err);
    }
  };

  return (
    <section className="mt-8" aria-labelledby="pending-invites">
      <h2 id="pending-invites" className="mb-2 text-sm font-semibold">
        Pending invites
      </h2>
      <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
        {invites.data.map((invite) => (
          <li
            key={invite.id}
            className="flex items-center gap-3 px-4 py-2.5"
            data-testid={`invite-${invite.email}`}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{invite.email}</p>
              <p className="text-xs text-fg-muted">
                Invited as {invite.role},{' '}
                {invite.expiresAt < Date.now()
                  ? 'expired'
                  : `expires in ${formatDistanceToNow(invite.expiresAt)}`}
                {invite.teamIds.length
                  ? ` · joins ${teamNames(invite.teamIds)}${invite.inheritTeamAccess || invite.role === 'admin' ? '' : ' (own access only)'}`
                  : ''}
                {invite.grants.length
                  ? ` · ${invite.limited ? 'only' : 'access to'} ${describeGrants(invite.grants, index)}`
                  : invite.role === 'guest' || invite.limited
                    ? ' · no places chosen'
                    : ''}
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void act(() => api.post(`/w/${ws}/invites/${invite.id}/resend`), 'Invite sent again')
              }
            >
              <RotateCw /> Resend
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void act(() => api.delete(`/w/${ws}/invites/${invite.id}`), 'Invite revoked')}
            >
              <Trash2 /> Revoke
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MembersPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const members = useMembers();
  const online = useOnlineIds();
  const canInvite = useCan('members.invite');
  const inviteOpen = Boolean(search.invite) && canInvite;
  const setInviteOpen = (open: boolean) =>
    void navigate({ search: open ? { invite: true } : {}, replace: true });

  return (
    <Page
      title="Members"
      icon={<Users />}
      toolbar={<SettingsTabs />}
      actions={
        canInvite ? (
          <Button size="sm" variant="primary" onClick={() => setInviteOpen(true)}>
            <UserPlus /> Invite people
          </Button>
        ) : undefined
      }
    >
      <div className="mx-auto max-w-4xl px-6 py-6">
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          {members.isLoading ? (
            <SkeletonRows rows={4} className="p-4" />
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border text-xs font-medium text-fg-subtle">
                  <th className="py-2 pr-3 pl-4 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Role</th>
                  <th className="py-2 pr-4 pl-3" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {(members.data ?? []).map((m) => (
                  <MemberRow key={m.id} member={m} online={online.has(m.id)} />
                ))}
              </tbody>
            </table>
          )}
        </div>
        {canInvite ? <PendingInvites /> : null}
      </div>
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </Page>
  );
}
